import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Tag, 
  Button, 
  Space, 
  Select,
  Statistic,
  Row,
  Col,
  message,
  Spin
} from 'antd';
import { 
  EyeOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  ClockCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminApi, TryOnFeedbackItem } from '../lib/api';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

const TryOnFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<TryOnFeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const loadFeedback = async (page: number = 1, status?: string) => {
    try {
      setLoading(true);
      const response = await adminApi.getTryOnFeedback({
        limit: pagination.pageSize,
        offset: (page - 1) * pagination.pageSize,
        status: status,
        sort_by: 'created_at',
        sort_order: 'desc'
      });

      setFeedback(response.items);
      setTotal(response.total);
      setCounts(response.counts);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.total
      }));
    } catch (error: any) {
      message.error('Failed to load feedback: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback(1, statusFilter);
  }, [statusFilter]);

  const handleStatusFilterChange = (value: string | undefined) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination: any) => {
    loadFeedback(newPagination.current, statusFilter);
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
      pending: { color: 'orange', icon: <ClockCircleOutlined /> },
      approved: { color: 'green', icon: <CheckCircleOutlined /> },
      rejected: { color: 'red', icon: <CloseCircleOutlined /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  const getIssueLabels = (issues: string[]): Record<string, string> => {
    const labels: Record<string, string> = {
      clothes_not_applied: "Clothes don't apply",
      person_changed: "Person changed",
      poor_quality: "Poor quality",
      missing_items: "Missing items",
      wrong_position: "Wrong position",
      other: "Other"
    };
    
    return issues.reduce((acc, issue) => {
      acc[issue] = labels[issue] || issue;
      return acc;
    }, {} as Record<string, string>);
  };

  const columns: ColumnsType<TryOnFeedbackItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <code style={{ fontSize: '12px' }}>{id.substring(0, 8)}...</code>
      )
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 120,
      render: (userId: string) => (
        <code style={{ fontSize: '12px' }}>{userId.substring(0, 8)}...</code>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status)
    },
    {
      title: 'Issues',
      dataIndex: 'feedback_issues',
      key: 'feedback_issues',
      render: (issues: string[]) => {
        const labels = getIssueLabels(issues);
        return (
          <Space size={[4, 4]} wrap>
            {issues.slice(0, 2).map(issue => (
              <Tag key={issue} style={{ margin: 0 }}>
                {labels[issue]}
              </Tag>
            ))}
            {issues.length > 2 && (
              <Tag style={{ margin: 0 }}>+{issues.length - 2} more</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Message',
      dataIndex: 'custom_message',
      key: 'custom_message',
      ellipsis: true,
      render: (message: string) => (
        message ? (
          <span style={{ fontSize: '12px', color: '#666' }}>
            <MessageOutlined style={{ marginRight: 4 }} />
            {message.substring(0, 50)}{message.length > 50 ? '...' : ''}
          </span>
        ) : (
          <span style={{ color: '#ccc' }}>No message</span>
        )
      )
    },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: 'Refunded',
      dataIndex: 'refunded',
      key: 'refunded',
      width: 100,
      render: (refunded: boolean, record: TryOnFeedbackItem) => (
        refunded ? (
          <Tag color="green">
            ✓ {record.refund_amount} ZAR
          </Tag>
        ) : record.status === 'approved' ? (
          <Tag color="orange">Pending</Tag>
        ) : (
          <Tag color="default">N/A</Tag>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: any, record: TryOnFeedbackItem) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/feedback/${record.id}`)}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>
          Try-On Feedback
        </h1>
        <p style={{ color: '#666', marginTop: 8, marginBottom: 0 }}>
          Review and manage user feedback on virtual try-on results
        </p>
      </div>

      {/* Status Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Review"
              value={counts.pending}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Approved"
              value={counts.approved}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Rejected"
              value={counts.rejected}
              prefix={<CloseCircleOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Feedback"
              value={counts.total}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>Status:</span>
          <Select
            style={{ width: 200 }}
            placeholder="All Statuses"
            allowClear
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <Option value={undefined}>All</Option>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
        </Space>
      </Card>

      {/* Feedback Table */}
      <Card>
        <Table
          loading={loading}
          columns={columns}
          dataSource={feedback}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default TryOnFeedback;
